import { normalizeRoleName } from './roles';

export type ProfileId = 'P01' | 'P02' | 'P03' | 'P04' | 'P05' | 'P06' | 'P07' | 'P08' | 'P09' | 'P10' | 'P11' | 'P12' | 'P13' | 'P14' | 'P15' | 'P16' | 'P17' | 'P18';
export interface ProfileUser {
  id?: string;
  role?: { name?: string | null } | null;
  designation?: string | null;
  department?: { name?: string | null; code?: string | null } | null;
  tenantId?: string | null;
  isSuperAdmin?: boolean;
}

const ROLE_PROFILES: Record<string, ProfileId> = {
  sr_sales_manager: 'P01', coordinator: 'P02', lead_triage_coordinator: 'P03',
  operations_manager: 'P04', buyer: 'P05', accounts_approver: 'P06',
  production_manager: 'P07', qa_manager: 'P10', qc_inspector: 'P11',
  dispatch_manager: 'P12', loader: 'P13', store_keeper: 'P14', security: 'P15',
  service_tech: 'P16', hr: 'P17', admin: 'P18', owner: 'P18', super_admin: 'P18',
};

export function resolveProfile(user: ProfileUser | null | undefined): ProfileId | null {
  if (!user) return null;
  const role = normalizeRoleName(user.role?.name);
  if (ROLE_PROFILES[role]) return ROLE_PROFILES[role];
  const title = (user.designation ?? '').toLowerCase();
  // A production role without exactly one station must not get the whole floor.
  if (role === 'production') {
    const puf = /\bpuf\b/.test(title);
    const packing = /\bpacking\b/.test(title);
    return puf === packing ? null : puf ? 'P08' : 'P09';
  }
  // Designation fallback is only for an absent role, never an unknown role.
  if (!role) {
    const designationProfiles: Record<string, ProfileId> = {
      'senior sales manager': 'P01', 'sales coordinator': 'P02',
      'lead triage coordinator': 'P03', 'crm operations': 'P04',
      'buyer': 'P05', 'accounts approver': 'P06', 'production manager': 'P07',
      'puf operator': 'P08', 'packing operator': 'P09', 'qa manager': 'P10',
      'qa inspector': 'P11', 'dispatch manager': 'P12', 'loader': 'P13',
      'store keeper': 'P14', 'security guard': 'P15', 'service technician': 'P16', 'hr': 'P17',
    };
    return designationProfiles[title.trim()] ?? null;
  }
  return null;
}

// Server-provided role/flag + absence of a tenant, never a magic email or UI view.
export function isPlatformAdmin(user: ProfileUser | null | undefined): boolean {
  if (!user || user.tenantId !== null) return false;
  return user.isSuperAdmin === true || ['admin', 'super_admin'].includes(normalizeRoleName(user.role?.name));
}

const LANDINGS: Record<ProfileId, string> = {
  "P01": "/dashboard",
  "P02": "/dashboard",
  "P03": "/leads/triage",
  "P04": "/dashboard",
  "P05": "/purchase-dashboard",
  "P06": "/dashboard",
  "P07": "/manufacturing/dashboard",
  "P08": "/my-shift",
  "P09": "/my-shift",
  "P10": "/quality",
  "P11": "/inspect",
  "P12": "/warehouse/dispatch-dashboard",
  "P13": "/loading",
  "P14": "/warehouse/dashboard",
  "P15": "/gate",
  "P16": "/service/my-day",
  "P17": "/dashboard",
  "P18": "/dashboard"
};
export const PROFILE_LABELS: Record<ProfileId, string> = {
  "P01": "Senior sales manager",
  "P02": "Sales coordinator / field sales",
  "P03": "Lead triage coordinator",
  "P04": "CRM operations",
  "P05": "Buyer",
  "P06": "Accounts approver",
  "P07": "Production manager",
  "P08": "PUF operator",
  "P09": "Packing operator",
  "P10": "QA manager",
  "P11": "QA inspector",
  "P12": "Dispatch manager",
  "P13": "Loader",
  "P14": "Store keeper",
  "P15": "Security guard",
  "P16": "Service technician",
  "P17": "HR",
  "P18": "Owner / admin"
};
export function profileLanding(profile: ProfileId): string { return LANDINGS[profile]; }
export function userLanding(user: ProfileUser | null | undefined): string {
  if (isPlatformAdmin(user)) return '/admin';
  const profile = resolveProfile(user);
  return profile ? profileLanding(profile) : '/no-access';
}

const LEGACY_ROLES = new Set(['manager', 'sales_manager', 'manager_sales', 'team_head', 'sales_head', 'lead_manager', 'telecaller', 'backend_staff', 'backend_crm', 'tele_sales', 'field_executive', 'sales_executive', 'sales_rep', 'sales_exec', 'accountant', 'service_manager', 'project_manager', 'field_partner', 'warehouse_executive', 'accounts_executive']);
export function isLegacyIdentity(user: ProfileUser | null | undefined): boolean {
  return LEGACY_ROLES.has(normalizeRoleName(user?.role?.name));
}

// 2026-09-16 amendment: explicit sales roles, not lead-handling or department heuristics.
const PERSONAL_SALES_ROLES = new Set(['sr_sales_manager','coordinator','sales_manager','manager_sales','team_head','sales_head','telecaller','backend_staff','tele_sales','field_executive','sales_executive','sales_rep','sales_exec']);
export function isSalesIdentity(user: ProfileUser | null | undefined): boolean {
  if (!user) return false;
  const role = normalizeRoleName(user.role?.name);
  return role ? PERSONAL_SALES_ROLES.has(role) : ['P01', 'P02'].includes(resolveProfile(user) ?? '');
}
